import { type Ref, computed, ref, toValue } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useEditStore } from "@/composables/edit-manager";
import { useDialog } from "primevue/usedialog";
import { useToast } from "primevue/usetoast";
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import { type BpmnArtefact } from "@/lib/artefacts";
import { useProcessStore } from "@/composables/process-manager";
import DefinitionGeneratorForm from "@/components/Process/DefinitionGeneratorForm.vue";

export const useGenerateBpmnDialog = ({
    artefact,
}: {
    artefact: Ref<BpmnArtefact | undefined>;
}) => {
    const route = useRoute();
    const router = useRouter();
    const toast = useToast();
    const dialog = useDialog();
    const dialogRef = ref<DynamicDialogInstance & { visible?: boolean }>();
    const isVisible = computed(() => Boolean(dialogRef.value?.visible));
    const isGenerating = ref(false);
    const { generateProcessDefinitionXml } = useProcessStore();
    const { startEditing } = useEditStore();

    /**
     * Generate the process definition and set it to the model.value.
     */
    const generateProcessDefinition = async (description: string) => {
        if (!description || !toValue(artefact)) {
            return;
        }

        isGenerating.value = true;
        try {
            // Navigate to the definition and start editing.
            await router.push({
                name: "processDefinitionPage",
                params: route.params,
            });
            startEditing();

            try {
                const xml = await generateProcessDefinitionXml(description);

                if (!xml) {
                    toast.add({
                        severity: "error",
                        summary: "No XML content generated.",
                    });
                    return;
                }

                toValue(artefact)?.updateContent(xml);

                console.log(
                    "Process Definition Generation - XML mode completed"
                );
            } catch (error) {
                console.error("Error generating BPMN XML directly:", error);
                toast.add({
                    severity: "error",
                    summary: `Could not generate BPMN XML: ${error}`,
                });
            }
        } catch (error) {
            console.error("Error in generateProcessDefinition:", error);
            toast.add({
                severity: "error",
                summary: `Error generating process definition: ${error}`,
            });
        } finally {
            isGenerating.value = false;
        }
    };

    const open = () => {
        dialog.open(DefinitionGeneratorForm, {
            props: {
                header: "Generate a first draft",
                style: {
                    maxWidth: "600px",
                    width: "80%",
                },
                modal: true,
                maximizable: false,
                draggable: false,
            },
            data: {
                generateCallback: generateProcessDefinition,
                getIsGenerating: () => isGenerating.value,
            },
        });
    };

    return { open, isVisible, isGenerating };
};
